<template>
  <div class="dashboard-consumption-breakdown">
    <div class="cb-header">
      <h3 class="cb-title">{{ $t('org-dashboard.consumption-breakdown') }}</h3>
      <div class="cb-view-modes" v-if="viewModes && viewModes.length > 1">
        <v-btn-toggle :value="activeViewMode" @change="$emit('update:activeViewMode', $event)" mandatory dense class="cb-toggle">
          <v-btn x-small :value="mode.value" v-for="mode in viewModes" :key="mode.value">{{ mode.label }}</v-btn>
        </v-btn-toggle>
      </div>
    </div>
    <div class="cb-body">
      <div class="cb-donut-wrap">
        <highcharts :options="chartOptions" ref="chart" />
        <div class="cb-donut-center">
          <span class="cb-donut-subtitle">Total</span>
          <span class="cb-donut-value">{{ formatNumber(total) }}</span>
          <span class="cb-donut-unit">{{ totalUnit }}</span>
        </div>
      </div>
      <div class="cb-table-wrap">
        <table class="cb-table">
          <thead>
            <tr>
              <th>{{ $t('org-dashboard.segment') }}</th>
              <th class="text-right">{{ activeViewModeLabel }}</th>
              <th class="text-right">{{ $t('org-dashboard.change') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(seg, i) in segments" :key="i" class="cb-row">
              <td class="cb-segment-cell">
                <span class="cb-segment-dot" :style="{backgroundColor: seg.color}"></span>
                <span class="cb-segment-name">{{ seg.label }}</span>
              </td>
              <td class="text-right cb-value">{{ formatNumber(seg.value) }} {{ totalUnit }}</td>
              <td class="text-right">
                <span class="cb-change" :class="seg.change >= 0 ? 'cb-change--up' : 'cb-change--down'">
                  {{ seg.change >= 0 ? '+' : '' }}{{ seg.change }}%
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'DashboardConsumptionBreakdown',
  components: {
    highcharts: Chart,
  },
  props: {
    total: { type: [Number, String], default: 0 },
    totalUnit: { type: String, default: 'kWh' },
    segments: { type: Array, default: () => [] },
    viewModes: { type: Array, default: () => [] },
    activeViewMode: { type: String, default: '' },
  },

  computed: {
    activeViewModeLabel() {
      const mode = this.viewModes.find(m => m.value === this.activeViewMode)
      return mode ? mode.label : this.activeViewMode
    },

    hasData() {
      return this.segments && this.segments.length > 0 && this.segments.some(s => s.value > 0)
    },

    chartOptions() {
      const data = this.hasData
        ? this.segments.map(s => ({
            name: s.label,
            y: s.value,
            color: s.color,
          }))
        : [{ name: this.$t('org-dashboard.no-data'), y: 1, color: '#e5e7eb' }]

      return {
        chart: {
          type: 'pie',
          height: 200,
          width: 200,
          backgroundColor: 'transparent',
          margin: [0, 0, 0, 0],
        },
        title: { text: '' },
        credits: { enabled: false },
        tooltip: {
          enabled: this.hasData,
          pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)',
        },
        plotOptions: {
          pie: {
            innerSize: '68%',
            dataLabels: { enabled: false },
            borderWidth: 0,
            states: { hover: { brightness: this.hasData ? -0.05 : 0 } },
          },
        },
        series: [{ data }],
      }
    },
  },

  methods: {
    formatNumber(val) {
      if (val === null || val === undefined) return '-'
      if (typeof val === 'number') return val.toLocaleString()
      return val
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-consumption-breakdown {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.cb-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.cb-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.cb-toggle .v-btn {
  text-transform: none !important;
  font-size: 10px !important;
  letter-spacing: 0 !important;
  min-width: 40px !important;
}

.cb-body {
  display: flex;
  align-items: center;
  gap: 32px;
}

.cb-donut-wrap {
  flex-shrink: 0;
  position: relative;
}

.cb-donut-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.cb-donut-subtitle {
  font-size: 10px;
  color: #6b7280;
}

.cb-donut-value {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.cb-donut-unit {
  font-size: 10px;
  color: #9ca3af;
}

.cb-table-wrap {
  flex: 1;
}

.cb-table {
  width: 100%;
  border-collapse: collapse;

  thead th {
    font-size: 10px;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
    padding-bottom: 8px;
    border-bottom: 1px solid #f3f4f6;
    padding-left: 8px;
  }

  tbody td {
    padding: 10px 8px;
    font-size: 12px;
  }
}

.cb-row {
  border-bottom: 1px solid #f9fafb;
  transition: background 0.15s;

  &:hover {
    background: #f9fafb;
  }
}

.cb-segment-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cb-segment-dot {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}

.cb-segment-name {
  font-weight: 600;
  color: #374151;
}

.cb-value {
  font-weight: 500;
  color: #4b5563;
}

.cb-change {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;

  &--up {
    color: #059669;
    background: #ecfdf5;
  }
  &--down {
    color: #dc2626;
    background: #fef2f2;
  }
}

@media (max-width: 768px) {
  .cb-body {
    flex-direction: column;
  }
}
</style>
